import React from "react";
import { Container, Typography, Box, Button, Paper, Grid, Avatar, Divider, alpha, Chip } from "@mui/material";
import { motion, useInView } from "framer-motion";
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupsIcon from '@mui/icons-material/Groups';
import PublicIcon from '@mui/icons-material/Public';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const AboutPreview = () => {
  const stats = [
    { value: '20+', label: 'Years of Excellence', icon: <HistoryIcon />, color: '#C6A75E', description: 'Of numismatic expertise' },
    { value: '98%', label: 'Client Satisfaction', icon: <EmojiEventsIcon />, color: '#C6A75E', description: 'Positive feedback rate' },
  ];

  const achievements = [
    { icon: <HistoryIcon />, title: '40+ Years', description: 'Of numismatic excellence' },
  ];

  return (
    <Box sx={{ 
      py: { xs: 10, md: 16 },
      background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background pattern */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.03, 0.06, 0.03],
        }}
        transition={{ duration: 8, repeat: Infinity }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 2px 2px, #C6A75E 1.5px, transparent 0)',
          backgroundSize: '45px 45px',
        }}
      />

      {/* Floating particles */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            x: Math.random() * window.innerWidth, 
            y: Math.random() * window.innerHeight,
            scale: Math.random() * 0.3 + 0.1
          }}
          animate={{
            y: [null, -50, 50, -50],
            x: [null, 50, -50, 50],
          }}
          transition={{
            duration: Math.random() * 20 + 15,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            position: 'absolute',
            width: Math.random() * 3 + 1,
            height: Math.random() * 3 + 1,
            borderRadius: '50%',
            background: i % 3 === 0 ? '#C6A75E' : '#FFFFFF',
            opacity: 0.05,
            pointerEvents: 'none',
          }}
        />
      ))}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box sx={{ mb: 2 }}>
                <Chip
                  label="HERITAGE & TRUST"
                  sx={{
                    backgroundColor: alpha('#C6A75E', 0.1),
                    color: '#C6A75E',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
              
              <Typography variant="h2" sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: 'linear-gradient(135deg, #FFFFFF 0%, #C6A75E 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}>
                Trusted by Collectors
                <Box component="span" sx={{ color: '#C6A75E', display: 'block', background: 'none', WebkitBackgroundClip: 'unset' }}>
                  Worldwide
                </Box>
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.85)', fontSize: '1.1rem', lineHeight: 1.7 }}>
                With a global network of verified dealers and numismatic experts, 
                DiamondCoins connects elite buyers and sellers across continents. 
                Every coin is authenticated, graded, and insured for secure transactions.
              </Typography>

              {/* Achievement Cards */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {achievements.map((achievement, index) => (
                  <Grid item xs={12} sm={12} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Box
                        sx={{
                          textAlign: 'center',
                          p: 3,
                          borderRadius: 2,
                          backgroundColor: alpha('#C6A75E', 0.05),
                          border: `1px solid ${alpha('#C6A75E', 0.2)}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha('#C6A75E', 0.1),
                            transform: 'translateY(-5px)',
                          },
                        }}
                      >
                        <Box sx={{ color: '#C6A75E', mb: 1 }}>
                          {React.cloneElement(achievement.icon, { sx: { fontSize: 48 } })}
                        </Box>
                        <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                          {achievement.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          {achievement.description}
                        </Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              {/* Trust Badges */}
              <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                {['PCGS', 'NGC', 'ANACS', 'ICTA'].map((badge, index) => (
                  <motion.div
                    key={badge}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      sx={{
                        px: 3,
                        py: 1.2,
                        backgroundColor: alpha('#C6A75E', 0.1),
                        border: '1px solid',
                        borderColor: alpha('#C6A75E', 0.3),
                        borderRadius: 0,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: alpha('#C6A75E', 0.2),
                          transform: 'translateY(-3px)',
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#C6A75E' }}>
                        {badge} CERTIFIED
                      </Typography>
                    </Paper>
                  </motion.div>
                ))}
              </Box>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  variant="contained" 
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  href="/about"
                  sx={{ 
                    px: 6, 
                    py: 1.8,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #C6A75E 0%, #B89A4F 100%)',
                    color: '#000000',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #D4B86A 0%, #C6A75E 100%)',
                    },
                  }}
                >
                  Discover Our Heritage
                </Button>
              </motion.div>
            </motion.div>
          </Grid>

          {/* Right Stats */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: alpha('#000000', 0.6),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#C6A75E', 0.2)}`,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#C6A75E',
                          transform: 'translateY(-8px)',
                          boxShadow: `0 10px 30px ${alpha('#C6A75E', 0.2)}`,
                        },
                      }}
                    >
                      <Box 
                        component={motion.div}
                        whileHover={{ scale: 1.1 }}
                        sx={{ color: stat.color, mb: 2 }}
                      >
                        {React.cloneElement(stat.icon, { sx: { fontSize: 48 } })}
                      </Box>
                      <Typography 
                        variant="h2" 
                        sx={{ 
                          color: '#C6A75E', 
                          mb: 1,
                          fontWeight: 700,
                          fontSize: { xs: '2rem', md: '2.5rem' },
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 0.5 }}>
                        {stat.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        {stat.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutPreview;